/**
 * tenantStorage — localStorage prefixado pelo tenant corrente (MT-4.7).
 *
 * Por quê: dois logins de tenants (BusinessUnits) diferentes no MESMO
 * navegador compartilham o mesmo localStorage. Sem prefixo, filtros
 * salvos, rascunhos e agendamentos de um tenant "vazam" para o outro
 * (mesma classe de bug do vazamento de contexto em terminal compartilhado
 * do Security Sprint 1 — A15). Toda chave PERSISTENTE de dado operacional
 * deve passar por aqui; preferências cosméticas globais (tema, sidebar)
 * ficam de fora de propósito.
 *
 * Whitelist cosmética (NUNCA prefixar — vale para o navegador, não para
 * o tenant): "pharuerp:is-dark", "pharuerp:theme", "pharuerp:theme-mode",
 * "pharuerp:accent-color", "pharuerp:density", "pharuerp:animacoes",
 * "pharuerp:sidebar-collapsed", "dasherp:sidebar:collapsed",
 * "dasherp:sidebar:top-level-aberto",
 * "pharuerp:planejamento-sidebar-width", "pharuerp:planejamento-sidebar-collapsed".
 *
 * Fonte do tenant: sessão persistida pelo AuthContext
 * (usuario.business_unit_codigo do /api/me). Lida direto do localStorage
 * para não criar dependência circular AuthContext <-> tenantStorage.
 */

// Mantido em sincronia com STORAGE_KEY do AuthContext.jsx.
const SESSION_STORAGE_KEY = "pharuerp:access-session";

// Tenant desconhecido (deslogado / payload antigo sem business_unit).
const TENANT_FALLBACK = "un?";

// Prefixos de app conhecidos — restringem a varredura de limpeza para
// nunca tocar chave que não seja nossa (defensivo; mesmo origin já é só
// da app, mas custa nada).
// "dash-pcp:" e legado pre-rebrand: chaves antigas ainda existem ate a
// migracao de boot (index.jsx) drenar todo mundo.
const PREFIXOS_APP = ["pharuerp:", "dash-pcp:", "dash-erp:", "dasherp:"];

/**
 * Chaves DURÁVEIS: configuração do usuário que sobrevive ao logout.
 *
 * Continuam prefixadas por tenant (não vazam entre clientes) — apenas não são
 * varridas junto com o estado de sessão. Os agendamentos de exportação
 * morriam a cada logout, inclusive nos "logout fantasma" que o AuthContext já
 * monitora: o usuário voltava e encontrava a pasta configurada e zero
 * agendamentos, sem nenhuma mensagem.
 */
export const CHAVES_DURAVEIS = [
  "pharuerp:agendamentos:v1",
  "pharuerp:agendamentos:historico:v1",
  // Visões salvas dos Relatórios do Legado: o usuário nomeou e salvou de
  // propósito. Guardadas POR USUÁRIO dentro da chave (ver useEstadoRelatorio),
  // então sobreviver ao logout não mostra a visão de um para o outro.
  "pharuerp:relatorios-legado:visoes:v1",
  // "Marcar para conferir" do Cenário do PCP (Capacidade da Fábrica): a
  // conferência é na leitura seguinte da planilha, que pode vir depois de
  // sair da conta. Também por usuário dentro da chave (useMarcasReprogramacao).
  "pharuerp:capacidade-fabrica:reprogramacoes:v1",
];

/**
 * Prefixos DURÁVEIS: famílias de chave com id no nome. Aviso da Pharu que a
 * pessoa fechou continua fechado depois de sair e entrar (a chave já leva o
 * usuário e o id do aviso, então não mostra a escolha de um para o outro).
 */
export const PREFIXOS_DURAVEIS = ["pharuerp:aviso-pharu:fechado:"];

function ehDuravel(chaveSemTenant) {
  return CHAVES_DURAVEIS.includes(chaveSemTenant) || PREFIXOS_DURAVEIS.some((p) => chaveSemTenant.startsWith(p));
}

function lerUsuarioDaSessao() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.usuario || null;
  } catch {
    return null;
  }
}

/**
 * Código do tenant corrente ("UN1", ...). Fallback: id numérico da FK
 * `business_unit`; por último, TENANT_FALLBACK quando deslogado ou o
 * /api/me ainda não trouxe a unidade.
 */
export function codigoTenantCorrente() {
  const usuario = lerUsuarioDaSessao();
  const codigo = usuario?.business_unit_codigo || usuario?.business_unit;
  return codigo ? String(codigo) : TENANT_FALLBACK;
}

/** True quando há tenant real na sessão (não é o fallback "un?"). */
export function temTenantCorrente() {
  return codigoTenantCorrente() !== TENANT_FALLBACK;
}

/** Ex.: chaveTenant("dash-erp:pedidos:filtros") -> "UN1:dash-erp:pedidos:filtros". */
export function chaveTenant(chave) {
  return `${codigoTenantCorrente()}:${chave}`;
}

/**
 * Lê a chave prefixada. Retrocompat: se a prefixada não existe mas a
 * antiga (sem prefixo) existe, migra o valor UMA vez (copia + remove a
 * antiga). A migração só acontece com tenant real — deslogado, apenas lê
 * a antiga sem mover (mover para "un?:" perderia o valor no login).
 */
export function getItemTenant(chave) {
  if (typeof window === "undefined") return null;
  try {
    const prefixada = chaveTenant(chave);
    const valor = window.localStorage.getItem(prefixada);
    if (valor !== null) return valor;

    const legado = window.localStorage.getItem(chave);
    if (legado !== null && temTenantCorrente()) {
      window.localStorage.setItem(prefixada, legado);
      window.localStorage.removeItem(chave);
    }
    return legado;
  } catch {
    return null;
  }
}

export function setItemTenant(chave, valor) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(chaveTenant(chave), valor);
  } catch {
    // storage indisponível / cota — persistência é best-effort
  }
}

/** Remove a chave prefixada E a legada (higiene pós-migração). */
export function removeItemTenant(chave) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(chaveTenant(chave));
    window.localStorage.removeItem(chave);
  } catch {
    // storage indisponível — nada a limpar
  }
}

/**
 * Remove TODAS as chaves do tenant corrente (e as gravadas sob o
 * fallback "un?"). Chamar no logout, ANTES de a sessão ser removida do
 * localStorage — depois disso o código do tenant não é mais legível.
 * Complementa a varredura legada do clearSession (que cobre só chaves
 * não-prefixadas "pharuerp:*").
 */
/**
 * Limpezas extras do logout, de dados que não moram no localStorage (ex.: o
 * cache em IndexedDB da Área do PCP). Cada módulo registra a sua ao ser
 * carregado; `limparChavesDoTenant` chama todas. Falha de uma não impede as
 * outras nem o logout.
 */
const limpezasDoLogout = new Set();

export function aoLimparDadosDoTenant(limpeza) {
  limpezasDoLogout.add(limpeza);
  return () => limpezasDoLogout.delete(limpeza);
}

function rodarLimpezasDoLogout() {
  limpezasDoLogout.forEach((limpeza) => {
    try {
      limpeza();
    } catch {
      // best-effort, como o resto do logout
    }
  });
}

export function limparChavesDoTenant() {
  if (typeof window === "undefined") return;
  rodarLimpezasDoLogout();
  try {
    const prefixos = new Set([
      `${codigoTenantCorrente()}:`,
      `${TENANT_FALLBACK}:`,
    ]);
    // Chaves DURAVEIS: configuracao do usuario que nao pode morrer no logout.
    // Continuam prefixadas por tenant (nao vazam entre clientes) — so nao sao
    // varridas junto com o estado de sessao.
    const remover = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const chave = window.localStorage.key(i);
      if (!chave) continue;
      for (const prefixo of prefixos) {
        if (
          chave.startsWith(prefixo) &&
          PREFIXOS_APP.some((app) => chave.slice(prefixo.length).startsWith(app)) &&
          !ehDuravel(chave.slice(prefixo.length))
        ) {
          remover.push(chave);
          break;
        }
      }
    }
    remover.forEach((chave) => window.localStorage.removeItem(chave));
  } catch {
    // storage indisponível (modo privado etc) — best-effort
  }
}
